// Calls for the article editor: /api/v1/orders/<id>/content/posts/*
// (apps/api/src/framework/posts.ts). Contract: packages/shared/src/api.ts.

import type { ArticleEntry, ArticlePatch, ArticleSummary, ContentMedia, ContentPreview, ContentRevision } from '@hedgehog/shared'
import { api } from '@/lib/api'

export function articlesApi(orderId: string) {
  const base = `/orders/${encodeURIComponent(orderId)}/content`
  const post = (id: string) => `${base}/posts/${encodeURIComponent(id)}`
  return {
    list: (signal?: AbortSignal) => api<{ items: ArticleSummary[] }>(`${base}/posts`, { signal }),
    get: (id: string, signal?: AbortSignal) => api<ArticleEntry>(post(id), { signal }),
    create: (patch: ArticlePatch) => api<ArticleEntry>(`${base}/posts`, { method: 'POST', json: patch }),
    put: (id: string, patch: ArticlePatch) => api<ArticleEntry>(post(id), { method: 'PUT', json: patch }),
    publish: (id: string) => api<ArticleEntry>(`${post(id)}/publish`, { method: 'POST' }),
    unpublish: (id: string) => api<ArticleEntry>(`${post(id)}/unpublish`, { method: 'POST' }),
    remove: (id: string) => api<{ id: string; deleted: true }>(post(id), { method: 'DELETE' }),
    /** Deleted articles. */
    trash: (signal?: AbortSignal) => api<{ posts: ArticleSummary[] }>(`${base}/trash`, { signal }),
    undelete: (id: string) => api<ArticleEntry>(`${post(id)}/undelete`, { method: 'POST' }),
    discard: (id: string) => api<ArticleEntry>(`${post(id)}/discard`, { method: 'POST' }),
    versions: (id: string) => api<{ items: ContentRevision[] }>(`${post(id)}/revisions`),
    /** Puts an earlier published version in the draft. */
    restore: (id: string, version: string) => api<{ hasDraft: true }>(`${post(id)}/restore/${encodeURIComponent(version)}`, { method: 'POST' }),
    /** The page at `path` in edit mode (drafts shown), for the preview frame; also gives the site origin. */
    preview: (path: string, signal?: AbortSignal) => api<ContentPreview>(`${base}/preview?path=${encodeURIComponent(path)}`, { signal }),
    /** Uploads an image to the site's media library. */
    upload: (file: File, alt: string) =>
      api<ContentMedia>(`${base}/media`, {
        method: 'POST',
        body: file,
        headers: { 'content-type': file.type || 'application/octet-stream', 'x-hh-filename': encodeURIComponent(file.name), 'x-hh-alt': encodeURIComponent(alt) },
      }),
  }
}

export type ArticlesApi = ReturnType<typeof articlesApi>

/**
 * A new article's first save creates it and moves the address from /articles/new to its id.
 * The route loader takes the entry from here instead of fetching it, so the editor stays as is.
 */
export const justCreated = new Map<string, ArticleEntry>()

/** Site path of an article. */
export const articlePath = (slug: string) => `/${slug}/`

/** "Hello, World!" -> "hello-world". Copy of slugify in packages/framework/src/site/posts.ts: change both together. */
export function slugify(title: string): string {
  return title.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80).replace(/-+$/, '')
}

export const SLUG_RULE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** Absolute URL for an image stored as a site path (/_hh/media/...). */
export function siteUrl(origin: string | null, src: string): string {
  return src.startsWith('/') && !src.startsWith('//') && origin ? origin + src : src
}

/** Display URL of a stored image asset. */
export function assetSrc(origin: string | null, asset: { _ref?: string; url?: string } | null): string {
  if (!asset) return ''
  if (typeof asset.url === 'string' && asset.url) return siteUrl(origin, asset.url)
  return asset._ref ? siteUrl(origin, `/_hh/media/${asset._ref}`) : ''
}

const DATE = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
export function fmtDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : DATE.format(d)
}

/** Status label of an article. */
export function articleStatus(a: Pick<ArticleSummary, 'status' | 'hasDraft'>): { label: string; tone: 'draft' | 'live' | 'changes' | 'scheduled' } {
  if (a.status === 'published') return a.hasDraft ? { label: 'Published, with unpublished changes', tone: 'changes' } : { label: 'Published', tone: 'live' }
  if (a.status === 'scheduled') return { label: 'Scheduled', tone: 'scheduled' }
  return { label: 'Draft', tone: 'draft' }
}
